import React from 'react';
import styled from 'styled-components';
import { usePropertyDetail } from './hook';
import { ImageSlideshow } from '../../organisms/ImageSlideshow';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ReactComponent as TelegramIcon } from '../../../assets/telegram.svg';
import { ReactComponent as WhatsappIcon } from '../../../assets/whatsapp.svg';
import { ReactComponent as PhoneIcon } from '../../../assets/phone.svg';
import { ReactComponent as FacebookSvgIcon } from "../../../assets/facebook.svg";
import { ReactComponent as EmailIcon } from '../../../assets/email.svg';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SEO } from '../../atoms/SEO';
import { ShareButton } from '../../atoms/ShareButton';


export const PropertyDetailPage = () => {
  const { property, loading } = usePropertyDetail();
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams<{ id: string; type: string }>();
  const { t, currentLanguage } = useLanguage();

  const handleBack = () => {
    const state = location.state as { returnUrl?: string };
    const returnUrl = state?.returnUrl || '';

    if (type === 'commercial') {
      navigate(`/property/commercial${returnUrl}`);
    } else if (type === 'residential') {
      navigate(`/property/residential${returnUrl}`);
    } else if (type === 'popular-property') {
      navigate('/');
    } else {
      navigate('/');
    }
  };

  const getStatusLabel = (status: string) => {
    if (status === 'rent') return t('property.status.rent');
    if (status === 'sale') return t('property.status.sale');
    return property.property_status_display;
  };

  const getTranslatedName = (item: any) => {
    if (!item) return '';
    const lang = currentLanguage?.toLowerCase();
    if (lang?.startsWith('km') || lang?.startsWith('kh')) return item.name_kh || item.name;
    if (lang?.startsWith('zh')) return item.name_zh || item.name;
    return item.name;
  };

  const formatTitle = () => {
    const parts = [];

    if (property.bedrooms) {
      const isEnglish = currentLanguage?.startsWith('en');
      parts.push(`${property.bedrooms} ${property.bedrooms > 1 && isEnglish ? t('property.detail.bedroom') + 's' : t('property.detail.bedroom')},`);
    }

    if (property.property_type) {
      parts.push(getTranslatedName(property.property_type));
    }

    if (property.property_status) {
      parts.push(`${t('common.for')} ${getStatusLabel(property.property_status)}`);
    }

    if (property.district) {
      parts.push(`, ${getTranslatedName(property.district)}`);
    }

    if (property.commune) {
      parts.push(`, ${getTranslatedName(property.commune)}`);
    }

    return parts.join(' ');
  };

  if (loading) return (
    <PageWrapper>
      <Container>
        <LoadingWrap role="status" aria-live="polite">
          <Spinner />
          <LoadingText>{t('common.loading')}</LoadingText>
        </LoadingWrap>
      </Container>
    </PageWrapper>
  );
  if (!property) return (
    <PageWrapper>
      <Container>{t('property.detail.propertyNotFound')}</Container>
    </PageWrapper>
  );

  return (
    <PageWrapper>
      <Container>
        {property && (
          <SEO
            title={formatTitle()}
            description={property.description?.substring(0, 160)}
            image={Array.isArray(property.property_images) && property.property_images.length > 0
              ? property.property_images[0].image
              : undefined}
            url={window.location.href}
          />
        )}
        <BackButton onClick={handleBack}>{t('property.detail.back')}</BackButton>
        {Array.isArray(property.property_images) && property.property_images.length > 0 ? (
          <ImageSlideshow
            images={property.property_images}
            aspectRatio="16/9"
          />
        ) : (
          <NoImagesMessage>{t('property.detail.noImagesAvailable')}</NoImagesMessage>
        )}
        <PageLayout>
          <MainContent>
            <TopHeaderRow>
              <Title>{formatTitle()}</Title>
              <ShareButton
                propertyId={property.id}
                type={type}
                iconOnly
              />
            </TopHeaderRow>
            <CodeRow>
              <LeftSection>
                {property.youtube_link && (
                  <YoutubeButton href={property.youtube_link} target="_blank" rel="noopener noreferrer">
                    <YoutubeIcon /> {t('property.detail.watchFullVideo')}
                  </YoutubeButton>
                )}
              </LeftSection>
              <PropertyCode>{t('property.detail.propertyCode')}: {property.property_code}</PropertyCode>
            </CodeRow>
            <SectionDivider />
            {property.description && property.description.trim() !== '' && (
              <>
                <SectionTitle>{t('property.detail.propertyDescription')}</SectionTitle>
                <Description>{property.description}</Description>
              </>
            )}
            <SectionTitle>{t('property.detail.propertyDetails')}</SectionTitle>
            <DetailsGrid>
              <DetailsCol>
                <DetailRow><Label>{t('property.propertyType')}</Label><Value>{getTranslatedName(property.property_type)}</Value></DetailRow>
                <DetailRow><Label>{t('property.propertyStatus')}</Label><Value>{getStatusLabel(property.property_status)}</Value></DetailRow>
                {(() => {
                  const statusRaw = (property as any).property_status_display ?? (property as any).property_status ?? (property as any).propertyStatus;
                  const isSale = typeof statusRaw === 'string' && statusRaw.toLowerCase().includes('sale');
                  return (
                    <DetailRow>
                      <Label>{t('property.detail.price')}</Label>
                      <Value>${parseInt(property.price, 10).toLocaleString()}{isSale ? '' : ` ${t('property.detail.perMonth')}`}</Value>
                    </DetailRow>
                  );
                })()}
                <DetailRow><Label>{t('property.size')}</Label><Value>{property.size} {t('property.detail.sqm') || 'sqm'}</Value></DetailRow>
                <DetailRow><Label>{t('property.detail.landArea')}</Label><Value>{property.land_area}</Value></DetailRow>
              </DetailsCol>
              <DetailsCol>
                <DetailRow><Label>{t('property.detail.bedroom')}</Label><Value>{property.bedrooms}</Value></DetailRow>
                <DetailRow><Label>{t('property.detail.bathroom')}</Label><Value>{property.bathrooms}</Value></DetailRow>
                <DetailRow><Label>{t('property.detail.district')}</Label><Value>{getTranslatedName(property.district)}</Value></DetailRow>
                <DetailRow><Label>{t('property.detail.commune')}</Label><Value>{getTranslatedName(property.commune)}</Value></DetailRow>
                <DetailRow><Label>{t('property.detail.city')}</Label><Value>{getTranslatedName(property.city)}</Value></DetailRow>
              </DetailsCol>
            </DetailsGrid>
            {property.features && property.features.length > 0 && (
              <>
                <SectionTitle>{t('property.detail.features')}</SectionTitle>
                <FeaturesBox>
                  {property.features.map((f: any) => (
                    <FeatureItem key={f.id}>{getTranslatedName(f)}</FeatureItem>
                  ))}
                </FeaturesBox>
              </>
            )}
          </MainContent>
          <Sidebar>
            <SidebarSection>
              <SidebarTitle>{t('property.detail.contactUs')}</SidebarTitle>
              {/* <SidebarText>{t('property.detail.callOurTeam')}</SidebarText> */}
              <SidebarContactRow>
                <SidebarIcon>
                  <PhoneIcon />
                </SidebarIcon>
                <SidebarLink href="tel:+85585281712">
                  +855 85 281 712
                </SidebarLink>
              </SidebarContactRow>

            </SidebarSection>
            <SidebarDivider />
            <SidebarSection>
              <SidebarTitle>{t('property.detail.chatWithUs')}</SidebarTitle>
              <SidebarText>{t('property.detail.speakToOurTeam')}</SidebarText>
              <SidebarChatRow>
                <SidebarIcon>
                  <TelegramIcon />
                </SidebarIcon>
                <SidebarLink href="https://t.me/+85585281712" target="_blank" rel="noopener noreferrer">
                  {t('property.detail.telegram')}
                </SidebarLink>
              </SidebarChatRow>
              <SidebarContactRow>
                <SidebarIcon>
                  <WhatsappIcon />
                </SidebarIcon>
                <SidebarLink href="https://wa.me/85585281712" target="_blank" rel="noopener noreferrer">
                  {t('property.detail.whatsapp')}
                </SidebarLink>
              </SidebarContactRow>
              <SidebarChatRow>
                <SidebarIcon>
                  <FacebookSvgIcon />
                </SidebarIcon>
                <SidebarLink href="https://www.facebook.com/livingpp" target="_blank" rel="noopener noreferrer">
                  {t('property.detail.facebook')}
                </SidebarLink>
              </SidebarChatRow>
              <SidebarChatRow>
                <SidebarIcon>
                  <EmailIcon />
                </SidebarIcon>
                <SidebarLink href="mailto:Livingppbox@gmail.com" target="_blank" rel="noopener noreferrer">
                  Livingppbox@gmail.com
                </SidebarLink>
              </SidebarChatRow>
            </SidebarSection>
          </Sidebar>
        </PageLayout>
      </Container>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    margin-top: 20px;
  }
`;

const Container = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 24px 80px 24px;
  width: 100%;
  background: #ffffff;
  
  @media (max-width: 1200px) {
    max-width: 100%;
    padding: 30px 20px 80px 20px;
  }
  
  @media (max-width: 768px) {
    padding: 20px 20px 60px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 16px 40px 16px;
  }
`;

const TopHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-top: 32px;

  @media (max-width: 768px) {
    margin-top: 24px;
  }
`;

const Title = styled.h1`
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  margin: 0;
  color: #183153;
  line-height: 1.3;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    padding: 0 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    padding: 0 4px;
  }
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 0 0;
  gap: 16px;
  flex-wrap: wrap;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin: 12px 0 0 0;
    padding: 0 6px;
  }
  
  @media (max-width: 480px) {
    padding: 0 4px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const YoutubeButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e53935;
  color: #e53935;
  font-weight: 600;
  border-radius: 30px;
  padding: 8px 20px;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #ffeaea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(229, 57, 53, 0.2);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 6px 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 5px 14px;
  }
`;

const YoutubeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M23.498 6.186a2.994 2.994 0 0 0-2.11-2.116C19.228 3.5 12 3.5 12 3.5s-7.228 0-9.388.57A2.994 2.994 0 0 0 .502 6.186C0 8.347 0 12 0 12s0 3.653.502 5.814a2.994 2.994 0 0 0 2.11 2.116C4.772 20.5 12 20.5 12 20.5s7.228 0 9.388-.57a2.994 2.994 0 0 0 2.11-2.116C24 15.653 24 12 24 12s0-3.653-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#e53935" />
  </svg>
);

const PropertyCode = styled.div`
  color: #3a4a5a;
  font-size: 1rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid #d3d7db;
  margin: 32px 0 24px 0;
  
  @media (max-width: 768px) {
    margin: 24px 0 20px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.1rem, 3vw, 1.3rem);
  font-weight: 700;
  color: #183153;
  margin: 24px 0 12px 0;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    margin: 20px 0 10px 0;
    font-size: 1.1rem;
    padding: 0 6px;
  }
  
  @media (max-width: 480px) {
    margin: 16px 0 8px 0;
    font-size: 1rem;
    padding: 0 4px;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  color: #222;
  margin-bottom: 0;
  line-height: 1.6;
  padding: 0 8px;
  white-space: pre-wrap;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0 4px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  margin: 24px 0 0 0;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    margin: 20px 0 0 0;
    padding: 0 6px;
  }
  
  @media (max-width: 480px) {
    gap: 20px;
    margin: 16px 0 0 0;
    padding: 0 4px;
  }
`;

const DetailsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  color: #2a3a4a;
  border-bottom: 1px solid #bfc9d8;
  padding-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding-bottom: 5px;
  }
`;

const Label = styled.div`
  font-weight: 600;
  color: #183153;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const Value = styled.div`
  font-weight: 400;
  text-align: right;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const FeaturesBox = styled.div`
  background: #f3f6fa;
  border-radius: 10px;
  padding: 24px 20px;
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 20px 16px;
    gap: 12px 20px;
    margin-top: 12px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    padding: 16px 12px;
    gap: 10px;
    margin-top: 10px;
  }
`;

const FeatureItem = styled.div`
  font-size: 1rem;
  color: #183153;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const PageLayout = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 0;
  }
`;

const MainContent = styled.div`
  flex: 1 1 0%;
  width: 100%;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  color: #183153;
  font-size: 15px;
  font-weight: 500;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  padding: 10px 16px;
  transition: all 0.2s ease;
  text-decoration: none;
  
  &:hover {
    background: #e2e8f0;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }
  
  /* Custom arrow icon */
  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-left: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }
  
  &:hover::before {
    transform: rotate(40deg) translateX(-2px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 16px;
    padding: 8px 14px;
    gap: 6px;
    
    &::before {
      width: 8px;
      height: 8px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 14px;
    padding: 6px 12px;
    gap: 5px;
    
    &::before {
      width: 7px;
      height: 7px;
    }
  }
`;

const NoImagesMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #f8fafc;
  border-radius: 8px;
  color: #666;
  font-size: 1rem;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    padding: 40px 16px;
    font-size: 0.95rem;
  }
  
  @media (max-width: 480px) {
    padding: 30px 12px;
    font-size: 0.9rem;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 60px 20px;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e5eaf1;
  border-top-color: #183153;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: #607089;
  font-size: 0.95rem;
`;

const Sidebar = styled.div`
  width: 300px;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 24px;
  position: sticky;
  top: 20px;
  
  @media (max-width: 900px) {
    width: 100%;
    margin-top: 32px;
    position: static;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    margin-top: 20px;
  }
`;

const SidebarSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const SidebarText = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const SidebarContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarChatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarIcon = styled.span`
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const SidebarLink = styled.a`
  color: #333;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: #007bff;
    text-decoration: underline;
  }
`;

const SidebarDivider = styled.hr`
  border: none;
  border-top: 1px solid #ddd;
  margin: 24px 0;
`;
